import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { ListPerson } from "@/pages/Private/pages/List/schema/list";
import { useUpdateCopywritingOutputMutation } from "@/pages/Private/redux/list/list.api";

import {
	applyDraftEditsToCopywritingOutput,
	copywritingDraftsHaveEdits,
} from "./applyDraftEditsToCopywritingOutput.util";
import { Draft } from "./inboxDraft.types";

interface UseSaveCopywritingDraftParams {
	lead: ListPerson | undefined;
	drafts: Draft[];
	/**
	 * The lead id the current `drafts` were built for. When it doesn't match the
	 * lead, the drafts are stale (mid prospect-switch) and saving is disabled to
	 * avoid flashing the action on another prospect's drafts.
	 */
	draftsLeadId: number | undefined;
	onSnackbar: (message: string) => void;
}

/**
 * Persists the user's edits to the AI copywriting draft back onto the lead via
 * `PATCH list_person/:id/copywriting-output`. Overlays the current drafts onto a
 * copy of the original `copywritingOutput` and sends the full object — the backend
 * replaces the stored value and records the edits as tone-of-voice examples.
 */
export const useSaveCopywritingDraft = ({
	lead,
	drafts,
	draftsLeadId,
	onSnackbar,
}: UseSaveCopywritingDraftParams) => {
	const { t } = useTranslation();
	const [updateCopywritingOutput, { isLoading: isSaving }] = useUpdateCopywritingOutputMutation();

	// Only surface the Save action when the drafts belong to the current lead and
	// the user has actually altered a message.
	const canSave =
		draftsLeadId === lead?.id && copywritingDraftsHaveEdits(lead?.copywritingOutput, drafts);

	const saveDraft = useCallback(async () => {
		if (!lead?.id || lead.copywritingOutput?.type !== "full_sequence") {
			return;
		}

		const copywritingOutput = applyDraftEditsToCopywritingOutput(lead.copywritingOutput, drafts);

		if (!copywritingOutput) {
			return;
		}

		try {
			await updateCopywritingOutput({ id: lead.id, copywritingOutput }).unwrap();
			onSnackbar(t("inbox.copywritingDraft.saved"));
		} catch {
			onSnackbar(t("inbox.copywritingDraft.saveError"));
		}
	}, [lead?.id, lead?.copywritingOutput, drafts, onSnackbar, t, updateCopywritingOutput]);

	return { saveDraft, isSaving, canSave };
};
